/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { Injectable } from '@angular/core';
import { Theme } from '../enum/theme.enum';

export class LoginConfig {
  /**
   * Route vers la page de login
   */
  public loginRoute: string;

  /**
   * Url de login
   */
  public loginUrl = 'api/auth-default';

  /**
   * Url de logout
   */
  public logoutUrl = 'logout';

  /**
   * Url de la page d'erreur
   */
  public errorRoute?: string;

  /**
   * Route vers la page de demande de reset du password
   */
  public resetRequest = '/reset-request';
  /**
   * Route vers la page de reset du password
   */
  public resetPassword = '/reset-password';

  /**
   * Url de login azure
   */
  public azureConnection?: {
    tenant: string; // 'o365.infodata.lu';
    client: string; // '33d65a3c-1d41-4c3c-a24e-fa46ea3e6e21';
    redirectUri: string; // 'http://localhost:4001';
    logoutUri: string;
    api: string; // "auth-azure"
  };

  /**
   * Url de connection google
   */
  public googleConnection?: {
    client: string; // '33d65a3c-1d41-4c3c-a24e-fa46ea3e6e21';
    redirectUri: string; // 'http://localhost:4001';
    api: string; // "auth-google"
  };

  public portalConnection?: {
    portalUri: string; // 'https://portail.integrix.lu
    api: string; // auth-portal
    userAppsApi: string; // api/portail-apps
    imagesUri: string; // 'https://portail.integrix.lu/notifications-email/visu/'
    loadPortalApps: boolean; // true
  };

  public nfcConnection: {
    route: string; // 'auth-nfc'
  };

  /**
   * Afficher ou non la connexion standard login/password
   */
  public defaultConnection?: boolean;

  /**
   * Redirection extérieur après la partie login
   */
  public redirectAfterLogin?: boolean;

  /**
   * Une redirection extérieur a-t-elle lieu après la phase de login
   */
  public redirection?: string;

  /**
   * Faut-il réaliser une /init
   */
  public initApi = 'init';

  /**
   * show Company field and return information
   */
  public showCompanyField?: boolean;

  public authorizationEndPoint: string;

  public authorizationRoute = 'appId';

  public showSettingsButton?: boolean;
  public showPasswordReset = true;
  public showFooter = true;

  public beforeSettingsRoutes = [];
}
@Injectable({
  providedIn: 'root',
})
export class AppConfig {
  public autoBuildApi?: string;
  public cordova: boolean;
  public apiEndPoint: string;
  /**
   * Application name
   */
  public appName;
  /**
   * Unique id. Set and never changed
   */
  public appId: string;
  /**
   * server api to load and save settings
   */
  public settingsApi = 'settings';
  /**
   * Application version
   */
  public appVersion?: string;
  /**
   * Application version date
   */
  public appVersionDate?: string;
  /**
   * List of available language
   */
  public lang?: string[];
  /**
   * default theme
   */
  public theme?: Theme;
  /**
   * Default route after login if necessary
   */
  public defaultRoute?: string;
  /**
   * Logo path link (best way : store to assets)
   */
  public appLogoPath?: string;
  /**
   * Logo path link for PDF exports
   */
  public exportLogoPath?: string;
  /**
   * Login background image path
   */
  public lockerBackgroundPath?: string;
  /**
   * bottom-right corner app logo path
   */
  public companyLogoPath?: string;
  /**
   * Display footer on login page
   */
  public displayFooterLoginPage = true;
  /**
   * About us link
   */
  public aboutUsLink = 'https://infodata-group.eu';
  /**
   * Newsletter subscription link
   */
  public newsletterLink = 'https://infodata-group.eu/newsletter/';
  /**
   * Login config
   */
  public loginConfig = new LoginConfig();
  public db: {
    version: number;
  } = {
    version: 1,
  };
  /**
   * Invalidation du cache en seconde
   */
  public cache = 86400;
  /**
   * Name of connected user
   */
  public connectedUser?: string;
  /**
   * Structure to reserve base links to other plateform (ex: {ticket: https://ticket.integrix.lu})
   */
  public links: any = {};
  /**
   * Maintenance infos
   */
  public maintenance = {
    maintenanceLogoPath: '', // Maintenance logo path
    maintenanceMode: false, // Is maintenance enabled ?
    route: '/maintenance', // Route maintenance
  };
  /**
   * Changelog infos
   */
  public changelog = {
    enable: false,
    endpoint: 'https://changelogs.integrix.lu/',
    route: 'changelogs',
    mailto: 'info@infodata.lu',
    tel: '+352 33 16 48',
  };

  public websocketConfig = {
    url: '',
    maxRetry: 10,
    delayRetry: 5000,
    retryAfter: 300000,
  };

  public noInternetRoute = '/no-internet';

  /**
   * How to load models : options | $metadata
   */
  public dataModelMethod: 'options' | '$metadata' = 'options';

  /**
   * Init app config by external loading
   */
  init(result: any) {
    for (const attr in result) {
      switch (attr) {
        case 'loginConfig':
          this.initLoginConfig(result[attr]);
          break;
        case 'maintenance':
          this.initMaintenanceConfig(result[attr]);
          break;
        case 'changelog':
          this.initChangelogConfig(result[attr]);
          break;
        case 'websocketConfig':
          this.websocketConfig = {
            ...this.websocketConfig,
            ...result[attr],
          };
          break;
        default:
          this[attr] = result[attr];
      }
    }
    if (!this.db || !this.db.version) {
      this.db = {
        version: 1,
      };
    }
  }

  private initChangelogConfig(config: any) {
    for (const attr in config) {
      /* istanbul ignore else */
      if (config[attr] !== undefined) {
        this.changelog[attr] = config[attr];
      }
    }
  }

  private initMaintenanceConfig(config: any) {
    for (const attr in config) {
      /* istanbul ignore else */
      if (config[attr] !== undefined) {
        this.maintenance[attr] = config[attr];
      }
    }
  }

  private initLoginConfig(config: any) {
    for (const attr in config) {
      /* istanbul ignore else */
      if (config[attr] !== undefined) {
        this.loginConfig[attr] = config[attr];
      }
    }
    /* istanbul ignore else */
    if (
      this.loginConfig.azureConnection &&
      !this.loginConfig.azureConnection.logoutUri
    ) {
      this.loginConfig.azureConnection.logoutUri =
        window.location.origin + '/login';
    }
  }
}
